<template>
  <div class="container">
    <header class="text-center wizard-header-text">
      <h1>Impact of Disability Statement</h1>
      <p class="gb-text-3">Compose a statement about how an individual student's disability impacts their involvement and progress in the general education curriculum.</p>
    </header>
    <main v-if="iodVisibility">
      <ImpactOfDisabilityWizard
        v-if="!query.disability && !loadingOptions"
        :disability-options="disabilityOptions"
        :disability.sync="disability"
        :subject-options="subjectOptions"
        :subjects.sync="subjects"
        :impact-area-options="impactAreaOptions"
        :impact-areas.sync="impactAreas"
        :loading-options="loadingOptions"
      />
      <div class="row" v-else-if="!loadingOptions">
        <div class="col-md-3">
          <ImpactOfDisabilityFilters
            :disability-options="disabilityOptions"
            :disability.sync="disability"
            :subject-options="subjectOptions"
            :subjects.sync="subjects"
            :change-requires-confirmation="changeRequiresConfirmation"
            @confirmupdate:disability="(newVal) => confirmFilterChange(newVal, 'disability')"
            @confirmupdate:subjects="(newVal) => confirmFilterChange(newVal, 'subject')"
          />
        </div>
        <div class="col-md-9">
          <ImpactOfDisabilityResults
            :disability="disability"
            :disability-label="disabilityLabel"
            :subjects="subjects"
            :impact-area-options="impactAreaOptions"
            :impact-areas.sync="impactAreas"
            :change-requires-confirmation.sync="changeRequiresConfirmation"
            @confirmupdate:impactAreas="(newVal) => confirmFilterChange(newVal, 'impactArea')"
          />
        </div>
      </div>
    </main>
    <h2 v-else class="text-center">This page is not available.</h2>
    <SubmitFeedback
      :disability="disability"
      type="Impact of Disability Statement"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SubmitFeedback from '@/components/SubmitFeedback'
import { getAreasOfNeed } from '@/api/presentlevels.js'
import { getDisabilitiesAndImpactAreas } from '@/api/impact-of-disability.js'
import ImpactOfDisabilityWizard from '@/components/impact-of-disability/ImpactOfDisabilityWizard'
import ImpactOfDisabilityResults from '@/components/impact-of-disability/ImpactOfDisabilityResults'
import ImpactOfDisabilityFilters from '@/components/impact-of-disability/ImpactOfDisabilityFilters'
import { MessageBox } from 'uiv'
import { toolkitUrls } from '@/links'

export default {
  name: 'ImpactOfDisability',
  components: {
    SubmitFeedback,
    ImpactOfDisabilityWizard,
    ImpactOfDisabilityResults,
    ImpactOfDisabilityFilters
  },
  props: {
    query: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      disabilityOptions: [],
      disability: '',
      subjects: [],
      impactAreasByDisability: [],
      impactAreas: [],
      loadingOptions: false,
      changeRequiresConfirmation: false,
      confirmProps: {
        html: true,
        okText: 'Yes',
        cancelText: 'No',
        size: 'md',
        customClass: 'confirm-modal',
        reverseButtons: true,
        content: '<p>If you make this selection, your edits will be lost.</p><p>Do you want to navigate away from your edited statement?</p>'
      }
    }
  },
  computed: {
    ...mapGetters('contentProfile', ['profileId']),
    ...mapGetters('visibility', ['iodVisibility']),
    ...mapGetters('user', ['licenseType', 'licenseSubType', 'isPremium']),
    impactAreaOptions() {
      if (this.disability) {
        return this.impactAreasByDisability
          .get(this.disability)
          .sort((a, b) => parseInt(a.area_order) - parseInt(b.area_order))
      } else {
        return []
      }
    },
    subjectOptions() { return getAreasOfNeed({iodSubject: true}) },
    disabilityLabel() {
      if (this.disability) {
        return this.disabilityOptions.filter(elem => elem.value === this.disability)[0].label
      } else {
        return ''
      }
    }
  },
  methods: {
    async getAndSetDisabilitiesAndImpactAreas() {
      this.loadingOptions = true
      const disabilitiesAndImpactAreas = await getDisabilitiesAndImpactAreas(
        this.licenseSubType,
        this.profileId
      )
      if (disabilitiesAndImpactAreas.disabilities) {
        this.disabilityOptions = disabilitiesAndImpactAreas.disabilities.map(item => ({
          label: item.disability,
          value: item.disability_uuid,
          locked: this.isDisabilityLocked(item)
        }))
        this.impactAreasByDisability = new Map(disabilitiesAndImpactAreas.disabilities.map(item => [item.disability_uuid, item.areas_of_impact]))
      }
      this.loadingOptions = false
    },
    setSubjectsAndImpactAreasFromUrl() {
      if (this.query.subjects) {
        let subjects
        if (Array.isArray(this.query.subjects)) {
          subjects = this.query.subjects
        } else {
          subjects = [this.query.subjects]
        }
        this.subjects = subjects.filter(subject => this.subjectOptions.some(option => option.value === subject))
      } else {
        this.subjects = []
      }
      if (this.query.impactAreas) {
        if (Array.isArray(this.query.impactAreas)) {
          this.impactAreas = this.impactAreaOptions.filter(ia => this.query.impactAreas.includes(ia.area_uuid))
        } else {
          this.impactAreas = this.impactAreaOptions.filter(ia => ia.area_uuid === this.query.impactAreas)
        }
      } else {
        this.impactAreas =  []
      }
    },
    async confirmFilterChange(newVal, filterType) {
      try {
        await MessageBox.confirm(this.confirmProps)
        switch (filterType) {
          case 'disability':
            this.setNewDisability(newVal)
            break;
          case 'subject':
            this.setNewSubject(newVal)
            break;
          case 'impactArea':
            this.setNewImpactArea(newVal)
            break;
          default:
            break;
        }
        this.$sendEvent('Reset Statement Text')
      } catch {
        this.$sendEvent('Dismissed Statement Text Reset')
      }
    },
    setNewDisability(newVal) {
      this.disability = newVal
    },
    setNewSubject(newVal) {
      let newSubjects = [...this.subjects]
      if (this.subjects.includes(newVal)) {
        newSubjects = newSubjects.filter(subj => subj !== newVal)
      } else {
        newSubjects.push(newVal)
      }
      this.subjects = newSubjects
    },
    setNewImpactArea(newVal) {
      this.impactAreas = newVal
    },
    isDisabilityLocked(disabilityObj) {
      if (this.isPremium) {
        return false
      } else {
        return disabilityObj.areas_of_impact.every(ia => ia.trial_access === 'FALSE')
      }
    }
  },
  async created() {
    if (this.iodVisibility) {
      const disabilityFromUrl = this.query.disability || ''
      await this.getAndSetDisabilitiesAndImpactAreas()
      if (!this.disabilityOptions.some(option => option.value === disabilityFromUrl && !option.locked)) {
        this.disability = null
      } else {
        this.disability = disabilityFromUrl
        this.setSubjectsAndImpactAreasFromUrl()
      }
      if (!this.disability) {
        this.$sendEvent('Viewed Impact of Disability Wizard')
      }
    }
  },
  watch: {
    profileId: async function() {
      window.location.href = toolkitUrls.impactOfDisability
    },
    disability: function(newVal, oldVal) {
      if (oldVal !== '' && (oldVal !== newVal)) {
        this.impactAreas = []
      }
      if (oldVal !== '' && newVal !== '') {
        this.$sendEvent('Changed Impact of Disability Disability')
      }
    },
    subjects: function(newVal, oldVal) {
      if (newVal.length > oldVal.length) {
        let addedVal = newVal.find(subj => !oldVal.includes(subj))
        if (addedVal) {
          this.$sendEvent('Selected Impact of Disability Subject', { iod_selected_subject: addedVal })
        }
      } else {
        let removedVal = oldVal.find(subj => !newVal.includes(subj))
        if (removedVal) {
          this.$sendEvent('Removed Impact of Disability Subject', { iod_removed_subject: removedVal})
        }
      }
    },
    impactAreas: function(newVal, oldVal) {
      if (newVal.length > oldVal.length) {
        let addedVal = newVal.find(subj => oldVal.every(oldSubj => !oldSubj.area_uuid.includes(subj.area_uuid)))
        if (addedVal) {
          this.$sendEvent('Selected Impact of Disability Impact Area', { iod_selected_impact_area: addedVal.area_label })
        }
      } else if (oldVal.length) {
        let removedVal = oldVal.find(subj => newVal.every(newSubj => !newSubj.area_uuid.includes(subj.area_uuid)))
        if (removedVal) {
          this.$sendEvent('Removed Impact of Disability Impact Area', { iod_removed_impact_area: removedVal.area_label })
        }
      }

    },
    '$route.query.disability': function(to) {
      if (!to) {
        this.disability = ''
      }
    }
  },
  eventContext() {
    let eventContext = {
      ref: this.query.ref,
      iod_selected_disability: this.disabilityLabel,
      iod_selected_subjects: this.subjects,
      iod_selected_impact_areas: this.impactAreas?.map(ia => ia.area_label),
      iod_page: 'Results'
    }
    if (!this.query.disability) {
      eventContext['iod_page'] = 'Wizard'
    }
    return eventContext
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
header {
  margin-bottom: 60px;
}
.gb-text-3 {
  max-width: 800px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
}
/deep/ .mdc-checkbox__native-control:checked ~ .mdc-checkbox__background {
  border-color: @plPurple;
  background-color: @plPurple;
}
/deep/ .mdc-checkbox__background::before {
  background: @plPurple;
}
/deep/ .dropdown-menu > li > a:hover, .dropdown-menu > li > a:focus {
  background-color: @plPurple;
  color: #FFF;
}
</style>
<style lang="less">
.confirm-modal {
  .modal-body, .modal-footer {
    text-align: center;
  }
}
</style>
